import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Plus, Trash2, Edit, Users } from "lucide-react";
import SearchInput from "../../components/SearchInput";
import api from "../../services/api";
import type {
  Department,
  DepartmentFormData,
  DepartmentMember,
} from "../../types/admin.types";

/** Extract user-facing error message from an API error. */
function getApiErrorMessage(error: unknown): string {
  const err = error as {
    response?: { data?: { error?: string; code?: string } };
    message?: string;
  };
  return err.response?.data?.error || err.message || "Unknown error";
}

const DepartmentManagementTab: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentTypes, setDepartmentTypes] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(
    null,
  );
  const [viewingMembers, setViewingMembers] = useState<{
    department: Department;
    members: DepartmentMember[];
  } | null>(null);
  const [form, setForm] = useState<DepartmentFormData>({
    name: "",
    type: "",
    description: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchDepartments(), fetchDepartmentTypes()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchDepartments = async (): Promise<void> => {
    try {
      const response = (await api.admin.getDepartments()) as {
        data?: Department[];
      };
      setDepartments(response.data || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
      alert("Error fetching departments: " + getApiErrorMessage(error));
    }
  };

  const fetchDepartmentTypes = async (): Promise<void> => {
    try {
      const response = (await api.admin.getDepartmentTypes()) as {
        data?: string[];
      };
      setDepartmentTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching department types:", error);
    }
  };

  const handleCreate = (): void => {
    setEditingDepartment(null);
    setForm({ name: "", type: departmentTypes[0] || "", description: "" });
    setShowModal(true);
  };

  const handleEdit = (dept: Department): void => {
    setEditingDepartment(dept);
    setForm({
      name: dept.name,
      type: dept.type,
      description: dept.description || "",
    });
    setShowModal(true);
  };

  const handleSave = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    try {
      if (editingDepartment) {
        await api.admin.updateDepartment(editingDepartment.id, form);
        alert(t("departments.messages.updated"));
      } else {
        await api.admin.createDepartment(form);
        alert(t("departments.messages.created"));
      }
      setShowModal(false);
      fetchDepartments();
    } catch (error) {
      alert("Error saving department: " + getApiErrorMessage(error));
    }
  };

  const handleDelete = async (dept: Department): Promise<void> => {
    if (!window.confirm(t("departments.deleteConfirm", { name: dept.name }))) {
      return;
    }

    try {
      await api.admin.deleteDepartment(dept.id);
      alert(t("departments.messages.deleted"));
      fetchDepartments();
    } catch (error) {
      const err = error as {
        response?: { data?: { code?: string } };
      };
      if (err.response?.data?.code === "HAS_MEMBERS") {
        alert(t("departments.messages.hasMembers"));
      } else {
        alert("Error deleting department: " + getApiErrorMessage(error));
      }
    }
  };

  const handleViewMembers = async (dept: Department): Promise<void> => {
    try {
      const response = (await api.admin.getDepartmentMembers(dept.id)) as {
        data?: DepartmentMember[];
      };
      setViewingMembers({ department: dept, members: response.data || [] });
      setShowMembersModal(true);
    } catch (error) {
      alert("Error fetching members: " + getApiErrorMessage(error));
    }
  };

  const filteredDepartments = departments.filter((dept) => {
    const matchesSearch =
      !searchTerm ||
      dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dept.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || dept.type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return <div className="loading">{t("departments.loading")}</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <Building2 size={16} /> {t("departments.title")}
        </h2>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus size={16} /> {t("departments.addDepartment")}
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section" style={{ marginBottom: "20px" }}>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t("departments.searchPlaceholder")}
          showSearchIcon
        />

        <div className="filter-controls">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t("departments.filters.allTypes")}</option>
            {departmentTypes.map((type) => (
              <option key={type} value={type}>
                {t(`departments.types.${type}`, { defaultValue: type })}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Departments Table */}
      <div className="users-table-container">
        <table className="users-table" aria-label={t("departments.title")}>
          <thead>
            <tr>
              <th>{t("departments.table.name")}</th>
              <th>{t("departments.table.type")}</th>
              <th>{t("departments.table.description")}</th>
              <th>{t("departments.table.members")}</th>
              <th>{t("departments.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredDepartments.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#999",
                  }}
                >
                  {searchTerm || filterType !== "all"
                    ? t("departments.noResults.filtered")
                    : t("departments.noResults.empty")}
                </td>
              </tr>
            ) : (
              filteredDepartments.map((dept) => (
                <tr key={dept.id}>
                  <td style={{ fontWeight: 500 }}>{dept.name}</td>
                  <td>
                    <span className="status-badge active">
                      {t(`departments.types.${dept.type}`, {
                        defaultValue: dept.type,
                      })}
                    </span>
                  </td>
                  <td
                    style={{
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dept.description || "-"}
                  </td>
                  <td>
                    <button
                      className="btn-sm btn-secondary"
                      onClick={() => handleViewMembers(dept)}
                      title={t("departments.viewMembers")}
                    >
                      <Users size={14} /> {dept.member_count || 0}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-sm btn-secondary"
                        onClick={() => handleEdit(dept)}
                        title={t("common:actions.edit")}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="btn-sm btn-delete"
                        onClick={() => handleDelete(dept)}
                        title={t("common:actions.delete")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div
            className="modal-backdrop"
            onClick={() => setShowModal(false)}
            aria-hidden="true"
          />
          <div className="modal">
            <div className="modal-header">
              <h3>
                {editingDepartment
                  ? t("departments.modal.editTitle")
                  : t("departments.modal.title")}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                aria-label="Close modal"
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label htmlFor="dept-name">
                  {t("departments.modal.name")} *
                </label>
                <input
                  id="dept-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("departments.modal.namePlaceholder")}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="dept-type">
                  {t("departments.modal.type")} *
                </label>
                <select
                  id="dept-type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  required
                >
                  {departmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {t(`departments.types.${type}`, { defaultValue: type })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="dept-description">
                  {t("departments.modal.description")}
                </label>
                <textarea
                  id="dept-description"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder={t("departments.modal.descriptionPlaceholder")}
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  {t("common:actions.cancel")}
                </button>
                <button type="submit" className="btn-primary">
                  {editingDepartment
                    ? t("departments.modal.update")
                    : t("departments.modal.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {showMembersModal && viewingMembers && (
        <div className="modal-overlay">
          <div
            className="modal-backdrop"
            onClick={() => setShowMembersModal(false)}
            aria-hidden="true"
          />
          <div className="modal" style={{ maxWidth: "600px" }}>
            <div className="modal-header">
              <h3>
                {t("departments.membersModal.title", {
                  name: viewingMembers.department.name,
                })}
              </h3>
              <button
                type="button"
                onClick={() => setShowMembersModal(false)}
                aria-label="Close modal"
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div style={{ padding: "16px" }}>
              {viewingMembers.members.length === 0 ? (
                <p style={{ color: "#999", textAlign: "center" }}>
                  {t("departments.membersModal.noMembers")}
                </p>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>{t("departments.membersModal.username")}</th>
                      <th>{t("departments.membersModal.email")}</th>
                      <th>{t("departments.membersModal.role")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingMembers.members.map((member) => (
                      <tr key={member.id}>
                        <td>{member.username}</td>
                        <td>{member.email}</td>
                        <td>
                          <span className="status-badge active">
                            {member.role}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowMembersModal(false)}
              >
                {t("common:actions.close", { defaultValue: "Close" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagementTab;
